/**
 * Canonical paint color palette — single source of truth for web-app,
 * functions, and design automation.
 *
 * Every entry carries id + keywords so the backend keyword-matching system
 * (wallTreatments.js / presetColorRules.js) works directly off this list.
 */

const PAINT_COLORS = [
  // Neutrals
  {
    id: 'white',
    hex: '#FFFFFF',
    name: 'White',
    category: 'Neutrals',
    keywords: ['white'],
  },
  {
    id: 'linen',
    hex: '#F5F0EB',
    name: 'Linen',
    category: 'Neutrals',
    keywords: ['off-white', 'cream'],
  },
  {
    id: 'cream',
    hex: '#F2EDE3',
    name: 'Cream',
    category: 'Neutrals',
    keywords: ['cream'],
  },
  {
    id: 'greige',
    hex: '#C5BFAB',
    name: 'Greige',
    category: 'Neutrals',
    keywords: ['neutral', 'beige'],
  },
  {
    id: 'pewter',
    hex: '#9A9A8E',
    name: 'Pewter',
    category: 'Neutrals',
    keywords: ['gray', 'neutral'],
  },
  {
    id: 'graphite',
    hex: '#5C5C5C',
    name: 'Graphite',
    category: 'Neutrals',
    keywords: ['dark', 'charcoal'],
  },
  {
    id: 'charcoal',
    hex: '#3A3A3A',
    name: 'Charcoal',
    category: 'Neutrals',
    keywords: ['dark', 'charcoal'],
  },

  // Warm
  {
    id: 'sand',
    hex: '#D4C4A8',
    name: 'Sand',
    category: 'Warm',
    keywords: ['tan', 'beige'],
  },
  {
    id: 'wheat',
    hex: '#C8B88A',
    name: 'Wheat',
    category: 'Warm',
    keywords: ['tan', 'brown'],
  },
  {
    id: 'camel',
    hex: '#A8885A',
    name: 'Camel',
    category: 'Warm',
    keywords: ['brown'],
  },
  {
    id: 'terracotta',
    hex: '#C47943',
    name: 'Terracotta',
    category: 'Warm',
    keywords: ['brown', 'natural'],
  },
  {
    id: 'clay',
    hex: '#A0664B',
    name: 'Clay',
    category: 'Warm',
    keywords: ['brown', 'natural'],
  },

  // Cool
  {
    id: 'sage',
    hex: '#8FA671',
    name: 'Sage',
    category: 'Cool',
    keywords: ['green', 'sage'],
  },
  {
    id: 'seafoam',
    hex: '#9BBFAE',
    name: 'Seafoam',
    category: 'Cool',
    keywords: ['green'],
  },
  {
    id: 'dusty_blue',
    hex: '#7EADB8',
    name: 'Dusty Blue',
    category: 'Cool',
    keywords: ['blue'],
  },
  {
    id: 'steel',
    hex: '#8DA5B0',
    name: 'Steel',
    category: 'Cool',
    keywords: ['blue', 'gray'],
  },
  {
    id: 'slate',
    hex: '#89959E',
    name: 'Slate',
    category: 'Cool',
    keywords: ['gray', 'neutral'],
  },

  // Bold
  {
    id: 'forest',
    hex: '#3E5732',
    name: 'Forest',
    category: 'Bold',
    keywords: ['dark', 'green'],
  },
  {
    id: 'emerald',
    hex: '#2D6A4F',
    name: 'Emerald',
    category: 'Bold',
    keywords: ['dark', 'green'],
  },
  {
    id: 'deep_teal',
    hex: '#2A6065',
    name: 'Deep Teal',
    category: 'Bold',
    keywords: ['dark', 'blue'],
  },
  {
    id: 'navy',
    hex: '#223059',
    name: 'Navy',
    category: 'Bold',
    keywords: ['dark', 'navy', 'blue'],
  },
  {
    id: 'midnight',
    hex: '#1C1F33',
    name: 'Midnight',
    category: 'Bold',
    keywords: ['dark'],
  },
];

const PAINT_CATEGORIES = ['Neutrals', 'Warm', 'Cool', 'Bold'];

/**
 * Map from old 11-color catalog IDs to canonical IDs.
 * Used for backward compatibility with saved preset wallPaintColors.
 */
const OLD_PAINT_ID_TO_CANONICAL = {
  off_white: 'linen',
  warm_cream: 'cream',
  eggshell: 'white',
  khaki: 'sand',
  silver: 'slate',
  blue_gray: 'steel',
  dark_navy: 'navy',
};

/**
 * Cabinet-appropriate paint palette — a curated subset of the wall colours,
 * skipping the brightest and most saturated tones (terracotta, emerald) that
 * do not show up on kitchen or bath cabinetry.
 *
 * Lives here rather than in web-app because both clients need it: the web
 * Design Center's cabinet paint control and the iOS picker's, whose Swift
 * mirror (`PaintColors.swift`) is pinned against this list by
 * `swiftMirrorDrift.test.js`.
 *
 * `category` is data, not a comment — the pickers group by it, and sixteen
 * ungrouped swatches is a wall of colour with no way in.
 */
const CABINET_PAINT_COLORS = [
  { name: 'White', hex: '#FFFFFF', category: 'Whites' },
  { name: 'Linen', hex: '#F5F0EB', category: 'Whites' },
  { name: 'Cream', hex: '#F2EDE3', category: 'Whites' },

  { name: 'Greige', hex: '#C5BFAB', category: 'Greys' },
  { name: 'Pewter', hex: '#9A9A8E', category: 'Greys' },
  { name: 'Graphite', hex: '#5C5C5C', category: 'Greys' },
  { name: 'Charcoal', hex: '#3A3A3A', category: 'Greys' },

  { name: 'Sage', hex: '#8FA671', category: 'Muted' },
  { name: 'Seafoam', hex: '#9BBFAE', category: 'Muted' },
  { name: 'Dusty Blue', hex: '#7EADB8', category: 'Muted' },
  { name: 'Blue-Grey', hex: '#8DA5B0', category: 'Muted' },
  { name: 'Slate', hex: '#89959E', category: 'Muted' },

  { name: 'Forest', hex: '#3E5732', category: 'Deep' },
  { name: 'Navy', hex: '#223059', category: 'Deep' },
  { name: 'Midnight', hex: '#1C1F33', category: 'Deep' },
];

/** Group order for the cabinet paint pickers, light to dark. */
const CABINET_PAINT_CATEGORIES = ['Whites', 'Greys', 'Muted', 'Deep'];

/**
 * A paint colour's name from the palette, else its `#RRGGBB`. Takes the hex
 * with or without its `#`, in either case. The take-off names its wall,
 * ceiling and trim paint rows with it.
 */
function lookupPaintName(hex) {
  const normalized = hex.replace('#', '').toUpperCase();
  const match = PAINT_COLORS.find(
    (p) => p.hex.replace('#', '').toUpperCase() === normalized
  );
  return match ? match.name : `#${normalized}`;
}

module.exports = {
  PAINT_COLORS,
  lookupPaintName,
  PAINT_CATEGORIES,
  CABINET_PAINT_COLORS,
  CABINET_PAINT_CATEGORIES,
  OLD_PAINT_ID_TO_CANONICAL,
};
